import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap, filter, take } from 'rxjs/operators';
import { NbToastrService } from '@nebular/theme';
import { UserPermissionService } from './user-permission.service';

@Injectable({
  providedIn: 'root',
})
export class PageAccessGuard implements CanActivate {
  constructor(
    private router: Router,
    private permissionService: UserPermissionService,
    private toastrService: NbToastrService
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const requestedPath = this.normalizePath(route.url.join('/'));
    
    // Always allow access to home page to prevent infinite redirects
    if (requestedPath === 'admin' || requestedPath === '') {
      return of(true);
    }
    
    // If pages are already loaded, check immediately
    if (this.permissionService.arePagesLoaded()) {
      return of(this.checkPageAccess(requestedPath));
    }

    return this.permissionService.isLoaded$.pipe(
      filter(isLoaded => isLoaded === true),
      take(1),
      map(() => this.checkPageAccess(requestedPath)),
      catchError((error) => {
        console.error('Error in PageAccessGuard:', error);
        this.showUnauthorizedMessage();
        this.router.navigate(['/admin'], { replaceUrl: true });
        return of(false);
      })
    );
  }

  private checkPageAccess(requestedPath: string): boolean {
    const hasAccess = this.permissionService.hasAccessToPage(requestedPath);
    
    if (!hasAccess) {
      this.showUnauthorizedMessage();
      // Use replaceUrl to prevent back button issues and avoid redirect loops
      this.router.navigate(['/admin'], { replaceUrl: true });
      return false;
    }

    return true;
  }

  private showUnauthorizedMessage(): void {
    this.toastrService.danger(
      'You do not have permission to access this page.',
      'Access Denied',
      {
        duration: 5000,
        status: 'danger'
      }
    );
  }

  private normalizePath(path: string): string {
    return path ? path.replace(/^\/+/, '') : '';
  }
}
